/**
 * Shared axe titles and badge-name lists for series that use axes in the badge explorer
 * and in the "Attribuer un badge" modal. Single source of truth to avoid duplication and mismatches.
 */

import {
  CATALOG_KEY_CPS,
  CATALOG_KEY_MER,
  CATALOG_KEY_ORIENTER,
  CATALOG_KEY_SOFT_SKILLS,
  CARTOGRAPHIE_V1_1_CATALOG_KEYS,
  displayLabelForCatalogKey,
  isCpsCatalog,
  isMerCatalog,
  isOrienterCatalog,
  isSoftSkillsCatalog,
} from './catalogSeries';

export const METIERS_DE_LA_MER_SERIES = "Série Métiers de la mer";
export const COMPETENCES_ORIENTER_COLLEGE_SERIES = "Série Compétences à s'orienter - Collège";
/** Nom encore en base (rake de rename pas encore passé côté back). Affichage FE : voir SOFT_SKILLS_SERIES_NAME dans utils/badgeLevelLabels.ts, plus « Soft Skills 4LAB » nulle part (Patrick, 29/09). */
export const SOFT_SKILLS_SERIES = "Série TouKouLeur";

export function isSoftSkillsSeries(seriesName?: string | null): boolean {
  const n = (seriesName || "").toLowerCase();
  return (
    n.includes("toukouleur") ||
    n.includes("4lab") ||
    n.includes("compétences transversales") ||
    n.includes("competences transversales")
  );
}

/** One axis: title (for dropdown/label) and list of badge names belonging to that axis */
export interface BadgeAxe {
  id: string;
  title: string;
  badgeNames: string[];
  /** Optional image URL for explorer (Métiers de la mer only) */
  imageUrl?: string;
}

// --- Série Métiers de la mer ---
const METIERS_MER_AXE1_TITLE = "Axe 1: Vie en collectivité";
const METIERS_MER_AXE2_TITLE = "Axe 2: Expérience de la mer et sécurité";
const METIERS_MER_AXE3_TITLE = "Axe 3: Engagement et construction du projet professionnel";

const METIERS_MER_AXE1_BADGE_NAMES: string[] = [
  "Niveau 1 - Se situer et s'adapter dans un collectif",
  "Niveau 1 - Respect des autres",
  "Niveau 1 - Respect du vivant et de l'environnement",
  "Niveau 2 - Contribuer activement au collectif"
];

const METIERS_MER_AXE2_BADGE_NAMES: string[] = [
  "Niveau 1 - Comprendre et appliquer les règles de sécurité maritime",
  "Niveau 1 - Responsabilité",
  "Niveau 2 - Agir avec autonomie et sens marin"
];

const METIERS_MER_AXE3_BADGE_NAMES: string[] = [
  "Niveau 1 - S'impliquer dans un parcours de découverte",
  "Niveau 2 - Se projeter et construire des perspectives"
];

export const METIERS_MER_AXE1_IMAGE = "/badges_metiers_de_la_mer/axe_1_vie_en_collectivite/axe_1.png";
export const METIERS_MER_AXE2_IMAGE = "/badges_metiers_de_la_mer/axe_2_experience_de_la_mer_et_securite/axe_2.png";
export const METIERS_MER_AXE3_IMAGE = "/badges_metiers_de_la_mer/axe_3_engagement_et_construction_du_projet_professionnel/axe_3.png";

const METIERS_MER_AXES: BadgeAxe[] = [
  { id: 'metiers_mer_1', title: METIERS_MER_AXE1_TITLE, badgeNames: METIERS_MER_AXE1_BADGE_NAMES, imageUrl: METIERS_MER_AXE1_IMAGE },
  { id: 'metiers_mer_2', title: METIERS_MER_AXE2_TITLE, badgeNames: METIERS_MER_AXE2_BADGE_NAMES, imageUrl: METIERS_MER_AXE2_IMAGE },
  { id: 'metiers_mer_3', title: METIERS_MER_AXE3_TITLE, badgeNames: METIERS_MER_AXE3_BADGE_NAMES, imageUrl: METIERS_MER_AXE3_IMAGE }
];

// --- Série Compétences à s'orienter - Collège ---
const COMPETENCES_ORIENTER_AXE1_TITLE = "Axe 1 – CONNAITRE ET SAVOIR S'INFORMER SUR LE MONDE : Découverte des environnements scolaires, professionnels, économiques et sociaux";
const COMPETENCES_ORIENTER_AXE2_TITLE = "Axe 2 – SE DÉCOUVRIR ET S'AFFIRMER : identification de soi, de ses intérêts, de ses compétences, de ses valeurs";
const COMPETENCES_ORIENTER_AXE3_TITLE = "Axe 3 – SE CONSTRUIRE ET SE PROJETER DANS UN MONDE EN MOUVEMENT";

const COMPETENCES_ORIENTER_AXE1_BADGE_NAMES: string[] = [
  "Compétence 1 – Chercher et trier l'information",
  "Compétence 2 – Connaitre les personnes, lieux, ressources qui peuvent m'aider",
  "Compétence 3 – Apprendre à découvrir les parcours de formation",
  "Compétence 4 – Apprendre à découvrir les métiers et le monde du travail",
  "Compétence 5 – M'interroger sur les clichés"
];

const COMPETENCES_ORIENTER_AXE2_BADGE_NAMES: string[] = [
  "Compétence 1 – Apprendre à me connaitre",
  "Compétence 2 – Définir mes projets en fonction de qui je suis",
  "Compétence 3 – M'autoriser à rêver et à avoir des ambitions",
  "Compétence 4 – Savoir me présenter et m'affirmer",
  "Compétence 5 – Identifier ce que j'ai appris et ce que je sais faire"
];

const COMPETENCES_ORIENTER_AXE3_BADGE_NAMES: string[] = [
  "Compétence 1 – Accepter les imprévus et saisir les occasions",
  "Compétence 2 – M'ouvrir au monde et aux autres",
  "Compétence 3 – Me préparer aux transitions et aux changements",
  "Compétence 4 – Me projeter et comprendre les conséquences de mes choix"
];

const COMPETENCES_ORIENTER_AXES: BadgeAxe[] = [
  { id: 'competences_orienter_1', title: COMPETENCES_ORIENTER_AXE1_TITLE, badgeNames: COMPETENCES_ORIENTER_AXE1_BADGE_NAMES },
  { id: 'competences_orienter_2', title: COMPETENCES_ORIENTER_AXE2_TITLE, badgeNames: COMPETENCES_ORIENTER_AXE2_BADGE_NAMES },
  { id: 'competences_orienter_3', title: COMPETENCES_ORIENTER_AXE3_TITLE, badgeNames: COMPETENCES_ORIENTER_AXE3_BADGE_NAMES }
];

/** Series that use axes in the assignment modal (Axe dropdown) */
export const SERIES_WITH_AXES = [METIERS_DE_LA_MER_SERIES, COMPETENCES_ORIENTER_COLLEGE_SERIES] as const;

/** Series that use competence-progress UI in Ma Cartographie (greyed image, progress bar, legend) */
export const SERIES_WITH_COMPETENCE_PROGRESS = [
  COMPETENCES_ORIENTER_COLLEGE_SERIES,
  METIERS_DE_LA_MER_SERIES,
  CATALOG_KEY_ORIENTER,
  CATALOG_KEY_MER,
] as const;

export function isSeriesWithAxes(seriesName: string): boolean {
  return SERIES_WITH_AXES.includes(seriesName as typeof SERIES_WITH_AXES[number]);
}

/** Accepts catalog_key or display name (R1). */
export function isSeriesWithCompetenceProgress(seriesName: string): boolean {
  return (SERIES_WITH_COMPETENCE_PROGRESS as readonly string[]).includes(seriesName);
}

/**
 * Returns the list of axes for a series that uses axes, or empty array if the series does not use axes.
 */
export function getAxesForSeries(seriesName: string): BadgeAxe[] {
  if (seriesName === METIERS_DE_LA_MER_SERIES) return METIERS_MER_AXES;
  if (seriesName === COMPETENCES_ORIENTER_COLLEGE_SERIES) return COMPETENCES_ORIENTER_AXES;
  return [];
}

/**
 * Returns badge names for a given series and axe title (for filtering API badges by axe).
 */
export function getBadgeNamesForAxe(seriesName: string, axeTitle: string): string[] {
  const axes = getAxesForSeries(seriesName);
  const axe = axes.find((a) => a.title === axeTitle);
  return axe ? axe.badgeNames : [];
}

/** Badge name + level for Série Métiers de la mer (for building static badge list in Explorer) */
export interface MetiersMerBadgeDef {
  name: string;
  level: 'level_1' | 'level_2';
  axe: 1 | 2 | 3;
}

/**
 * Returns all Métiers de la mer badges with name and level, derived from axe badge names.
 * Single source of truth for names; level is inferred from "Niveau 1" / "Niveau 2" in the name.
 */
export function getMetiersMerBadgesWithLevel(): MetiersMerBadgeDef[] {
  const result: MetiersMerBadgeDef[] = [];
  METIERS_MER_AXES.forEach((axe, idx) => {
    const axeNum = (idx + 1) as 1 | 2 | 3;
    axe.badgeNames.forEach((name) => {
      const level: 'level_1' | 'level_2' = name.startsWith('Niveau 1 -') ? 'level_1' : 'level_2';
      result.push({ name, level, axe: axeNum });
    });
  });
  return result;
}
// --- Série TouKouLeur ("Compétences transversales" dans la spec cartographie V1.1) ---
// Noms tirés de la spec KIN_UX_CARTOGRAPHIE_V1_1 §3 (exemple complet : 3+2+4+2 = 11 compétences,
// qui correspond exactement au total annoncé). PAS vérifiés directement contre la base de données
// (accès DB indisponible pour cette passe) — à confirmer avec Fatima ; une compétence dont le nom ne
// matche aucune liste ci-dessous tombe dans un groupe "Non classé" plutôt que d'être mal affectée.
export const TOUKOULEUR_SERIES = SOFT_SKILLS_SERIES;

const TOUKOULEUR_AXE_RELATIONNEL_TITLE = "Relationnel";
const TOUKOULEUR_AXE_PERSONNEL_TITLE = "Personnel";
const TOUKOULEUR_AXE_METHODE_TITLE = "Méthode";
const TOUKOULEUR_AXE_CREATION_TITLE = "Création";

const TOUKOULEUR_AXE_RELATIONNEL_BADGE_NAMES: string[] = ["Communication", "Coopération", "Sociabilité"];
const TOUKOULEUR_AXE_PERSONNEL_BADGE_NAMES: string[] = ["Adaptabilité", "Engagement"];
const TOUKOULEUR_AXE_METHODE_BADGE_NAMES: string[] = [
  "Esprit Critique",
  "Formation",
  "Gestion de Projet",
  "Organisation Opérationnelle",
];
const TOUKOULEUR_AXE_CREATION_BADGE_NAMES: string[] = ["Créativité", "Information Numérique"];

const TOUKOULEUR_AXES: BadgeAxe[] = [
  { id: 'toukouleur_relationnel', title: TOUKOULEUR_AXE_RELATIONNEL_TITLE, badgeNames: TOUKOULEUR_AXE_RELATIONNEL_BADGE_NAMES },
  { id: 'toukouleur_personnel', title: TOUKOULEUR_AXE_PERSONNEL_TITLE, badgeNames: TOUKOULEUR_AXE_PERSONNEL_BADGE_NAMES },
  { id: 'toukouleur_methode', title: TOUKOULEUR_AXE_METHODE_TITLE, badgeNames: TOUKOULEUR_AXE_METHODE_BADGE_NAMES },
  { id: 'toukouleur_creation', title: TOUKOULEUR_AXE_CREATION_TITLE, badgeNames: TOUKOULEUR_AXE_CREATION_BADGE_NAMES },
];

// --- Série Compétences Psychosociales (nom exact DB, vérifié contre BadgeSeries::CPS_SERIES_NAME
// et db/competences_psychosociales.rb le 29/09 : pas de préfixe « Série », 3 axes / 6 compétences / 17 badges) ---
export const PSYCHOSOCIALES_SERIES = "Compétences psychosociales";

const CPS_AXE_COGNITIVES_TITLE = "Cognitives";
const CPS_AXE_EMOTIONNELLES_TITLE = "Émotionnelles";
const CPS_AXE_SOCIALES_TITLE = "Sociales";

// Valeurs = Badge.name réels (le badge = l'item C1.1/C2.3/etc., pas le nom de la compétence C1/C2/etc.
// — vérifié directement contre db/competences_psychosociales.rb, 7+5+5 = 17 badges).
const CPS_AXE_COGNITIVES_BADGE_NAMES: string[] = [
  "Renforcer sa connaissance de soi (C1.1) et penser de façon critique (C1.2)",
  "Connaître ses valeurs, ses besoins psychologiques et ses buts personnels (C1.3) et Prendre des décisions constructives (C1.4)",
  "S'auto-évaluer positivement (C1.5)",
  "Renforcer sa pleine attention – à soi (C1.6)",
  "Atteindre ses buts personnels (C2.1)",
  "Gérer ses impulsions (C2.2)",
  "Résoudre des problèmes de façon créative et efficace (C2.3) & Savoir demander de l'aide (C2.4)",
];
const CPS_AXE_EMOTIONNELLES_BADGE_NAMES: string[] = [
  "Comprendre les émotions (E1.1)",
  "Identifier ses émotions (E1.2)",
  "Exprimer ses émotions de façon constructive (E2.1)",
  "Réguler ses émotions agréables (E.2.2.a) et désagréables (E2.2.b)",
  "Gérer son stress (E2.3)",
];
const CPS_AXE_SOCIALES_BADGE_NAMES: string[] = [
  "Communiquer de façon efficace et positive (S1.1)",
  "Communiquer de façon empathique (S1.2)",
  "Développer des liens et des comportements prosociaux (S1.3)",
  "S'affirmer (et résister à la pression sociale) par l'assertivité et le refus (S2.1)",
  "Résoudre les conflits de façon constructive (S2.2)",
];

const CPS_AXES: BadgeAxe[] = [
  { id: 'cps_cognitives', title: CPS_AXE_COGNITIVES_TITLE, badgeNames: CPS_AXE_COGNITIVES_BADGE_NAMES },
  { id: 'cps_emotionnelles', title: CPS_AXE_EMOTIONNELLES_TITLE, badgeNames: CPS_AXE_EMOTIONNELLES_BADGE_NAMES },
  { id: 'cps_sociales', title: CPS_AXE_SOCIALES_TITLE, badgeNames: CPS_AXE_SOCIALES_BADGE_NAMES },
];

/** Séries couvertes par la cartographie V1.1 (anneaux), dans l'ordre d'affichage des onglets. */
export const CARTOGRAPHIE_V1_1_SERIES = [
  TOUKOULEUR_SERIES,
  COMPETENCES_ORIENTER_COLLEGE_SERIES,
  METIERS_DE_LA_MER_SERIES,
  PSYCHOSOCIALES_SERIES,
] as const;

/** Prefer catalog keys for cartographie tabs (R1). */
export { CARTOGRAPHIE_V1_1_CATALOG_KEYS };

/** Nom du "3e étage" (niveau / CPS spécifique) selon la série — spec §1. */
export function getNiveauWordForSeries(seriesName: string): string {
  if (isCpsCatalog({ catalog_key: seriesName, series: seriesName })) return 'CPS spécifique';
  return 'Niveau';
}

// Séries dont les items de constat sont typés savoir / savoir-faire (BadgeSkill#category
// domain/expertise/both a un vrai sens de "Je connais" / "Je fais" pour ces items).
// P2.13 V1.2, écran 14 : « Trois de nos quatre séries — orientation, transversales, mer —
// n'ont pas ce typage : leurs items forment une liste simple. » Seule la série CPS
// déclare ce typage aujourd'hui — ne pas étendre cette liste sans vraies données typées
// côté back pour la série concernée (category: domain n'y signifie pas forcément "savoir",
// ex. Soft Skills où domain = "domaine d'engagement", un tout autre concept).
const SERIES_WITH_TYPED_SAVOIR_ITEMS: string[] = [PSYCHOSOCIALES_SERIES, CATALOG_KEY_CPS];

export function hasTypedSavoirSavoirFaireItems(seriesName: string): boolean {
  return SERIES_WITH_TYPED_SAVOIR_ITEMS.includes(seriesName);
}

/**
 * Retourne les axes de la cartographie V1.1 pour une série donnée, y compris TouKouLeur et CPS
 * (contrairement à getAxesForSeries, réservée aux séries de l'ancien flux "Attribuer une preuve").
 * Accepte catalog_key ou nom affiché (R1).
 * Retourne [] pour une série sans axe (ex. une série non listée ici).
 */
export function getCartographieAxesForSeries(seriesName: string): BadgeAxe[] {
  if (isSoftSkillsCatalog({ catalog_key: seriesName, series: seriesName })) return TOUKOULEUR_AXES;
  if (isCpsCatalog({ catalog_key: seriesName, series: seriesName })) return CPS_AXES;
  if (isMerCatalog({ catalog_key: seriesName, series: seriesName })) {
    return getAxesForSeries(displayLabelForCatalogKey(CATALOG_KEY_MER));
  }
  if (isOrienterCatalog({ catalog_key: seriesName, series: seriesName })) {
    return getAxesForSeries(displayLabelForCatalogKey(CATALOG_KEY_ORIENTER));
  }
  const asName =
    seriesName === CATALOG_KEY_MER ||
    seriesName === CATALOG_KEY_ORIENTER ||
    seriesName === CATALOG_KEY_SOFT_SKILLS ||
    seriesName === CATALOG_KEY_CPS
      ? displayLabelForCatalogKey(seriesName)
      : seriesName;
  return getAxesForSeries(asName);
}

// Re-export keys used by carto consumers
export { CATALOG_KEY_SOFT_SKILLS, CATALOG_KEY_CPS, CATALOG_KEY_ORIENTER, CATALOG_KEY_MER };
